/* eslint-disable no-unused-vars */
import { useState } from 'react';
import api from '../../services/api.service';
import GeneralModal from '../modals/GeneralModal';
import UploadProgressModal from '../modals/UploadProgrssModal';
import { AuthService } from '../../services/auth.service';

const ALLOWED_MIME_TYPES = [
  "application/zip",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/csv",
  "application/vnd.ms-excel",
  "application/x-zip-compressed",
];

export default function Home() {
  // ดึงข้อมูลผู้ใช้จาก session storage ผ่าน AuthService
  const userData = AuthService.getUserData();
  const hospitalName = userData?.hospitalName || "Hospital A"; // กำหนด default หากไม่มีข้อมูล

  // State สำหรับไฟล์และข้อมูลทั่วไป
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState([]);
  const [totalFileSize, setTotalFileSize] = useState(0);

  // State สำหรับ General Modal
  const [generalModal, setGeneralModal] = useState({
    isVisible: false,
    message: "",
    isSuccess: true,
    onClose: null,
  });

  // State สำหรับ Upload Progress Modal และข้อมูล upload
  const [uploadProgressModalVisible, setUploadProgressModalVisible] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedSize, setUploadedSize] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  // ฟังก์ชันแสดง General Modal
  const showGeneralModal = (message, isSuccess = true, onCloseCallback = null) => {
    setGeneralModal({
      isVisible: true,
      message,
      isSuccess,
      onClose: onCloseCallback,
    });
  };

  // ฟังก์ชันปิด General Modal (และเรียก callback ถ้ามี)
  const handleCloseGeneralModal = () => {
    setGeneralModal(prev => ({ ...prev, isVisible: false }));
    if (generalModal.onClose) {
      generalModal.onClose();
    }
  };

  // คำนวณขนาดไฟล์รวมและอัปเดต state
  const calculateTotalFileSize = (filesArray) => {
    const total = filesArray.reduce((sum, fileObj) => sum + fileObj.size, 0);
    setTotalFileSize(total);
  };

  // เมื่อมีการเลือกไฟล์ใหม่
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const newFiles = selectedFiles.map((file) => ({
      file,
      name: file.name,
      size: file.size,
      progress: 0,
    }));

    const invalidFiles = [];
    const validFiles = newFiles.filter((fileObj) => {
      if (
        fileObj.size <= 10 * 1024 * 1024 &&
        ALLOWED_MIME_TYPES.includes(fileObj.file.type)
      ) {
        return true;
      } else {
        if (fileObj.size > 10 * 1024 * 1024) {
          invalidFiles.push(
            `File ${fileObj.name} exceeds the maximum file size of 10 MB!`
          );
        }
        if (!ALLOWED_MIME_TYPES.includes(fileObj.file.type)) {
          invalidFiles.push(
            `File ${fileObj.name} is invalid. Only ZIP, XLSX, and CSV are allowed!`
          );
        }
        return false;
      }
    });

    if (invalidFiles.length > 0) {
      showGeneralModal(invalidFiles.join("\n"), false);
    }

    if (validFiles.length > 0) {
      const updatedFiles = [...files, ...validFiles];
      setFiles(updatedFiles);
      calculateTotalFileSize(updatedFiles);
    }
  };

  // ลบไฟล์ออกจากรายการที่เลือก
  const handleFileRemove = (indexToRemove) => {
    const updatedFiles = files.filter((_, index) => index !== indexToRemove);
    setFiles(updatedFiles);
    calculateTotalFileSize(updatedFiles);
  };

  // ฟังก์ชัน Clear รายการไฟล์ทั้งหมด
  const handleClear = () => {
    setFiles([]);
    setTotalFileSize(0);
  };

  // ฟังก์ชันสำหรับ Cancel Upload (ตัวอย่างสำหรับหยุดการอัปโหลด)
  const handleCancelUpload = () => {
    setIsUploading(false);
    setUploadProgressModalVisible(false);
    showGeneralModal("Upload cancelled", false);
  };

  const handleUpload = async () => {
    if (files.length === 0) return;

    setIsUploading(true);
    setUploadProgress(0);
    setUploadedSize(0);
    setUploadProgressModalVisible(true);

    try {
      const formData = new FormData();
      formData.append('hospitalName', hospitalName);

      files.forEach(fileObj => {
        // ส่งไฟล์พร้อมชื่อดั้งเดิม
        const file = fileObj.file;
        formData.append('fileUpload', file, file.name);
      });

      const response = await api.post('/sftp/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 30000,
        onUploadProgress: (progressEvent) => {
          const progress = (progressEvent.loaded / progressEvent.total) * 100;
          setUploadProgress(progress);
          setUploadedSize(progressEvent.loaded);
        }
      });

      console.log('Upload response:', response.data);
      setIsUploading(false);
      setUploadProgressModalVisible(false);
      showGeneralModal("Files uploaded successfully", true, () => {
        setFiles([]);
        setTotalFileSize(0);
        setUploadProgress(0);
        setUploadedSize(0);
      });

    } catch (error) {
      console.error('Upload error:', error);
      setIsUploading(false);
      setUploadProgressModalVisible(false);
      showGeneralModal(
        error.response?.data?.message || "Error uploading files",
        false
      );
    }
  };

  return (
    <>
      <div className="container mx-auto max-w-4xl px-3 sm:px-6 md:px-8 py-6 sm:py-8 my-4 sm:my-6">
        <div className="bg-white shadow-md rounded-xl p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-bold mb-2 sm:mb-4">Upload and attach files</h2>
          <p className="text-gray-500 mb-3 sm:mb-4 font-semibold text-sm sm:text-base">
            Upload and attach files to this CG Portal
          </p>

          {/* แสดง error (ถ้ามี) */}
          {errors.length > 0 && (
            <div className="text-red-500 mb-3 sm:mb-4 text-sm sm:text-base whitespace-pre-wrap">
              {errors.join("\n")}
            </div>
          )}
          {/* แสดงชื่อโรงพยาบาล */}
          <div className="mb-3 sm:mb-4 flex flex-col sm:flex-row sm:items-center">
            <label className="block text-gray-700 mb-1 sm:mb-0 sm:mr-2 text-sm sm:text-base">Hospital Name:</label>
            <span className="text-gray-800 font-semibold text-sm sm:text-base">{hospitalName}</span>
          </div>

          {totalFileSize > 0 && (
            <div className="mb-3 sm:mb-4">
              <p className="text-gray-600 text-sm sm:text-base">
                Total file size:{" "}
                <span className={`${totalFileSize / 1024 / 1024 > 9 ? "text-red-500" : "text-gray-800"} font-semibold`}>
                  {(totalFileSize / 1024 / 1024).toFixed(2)} MB
                </span>
              </p>
            </div>
          )}

          {/* File Upload Area */}
          <div className="border-2 border-dashed rounded-lg border-gray-300 p-3 sm:p-4 mb-3 sm:mb-4 flex flex-col items-center justify-center">
            <input
              type="file"
              multiple
              onChange={handleFileChange}
              className="hidden"
              id="file-upload"
              accept=".zip,.xlsx,.xls,.csv"
            />
            <label
              htmlFor="file-upload"
              className="cursor-pointer text-blue-500 flex flex-col items-center"
              tabIndex="0"
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  document.getElementById("file-upload").click();
                }
              }}
            >
              <i className="fa-solid fa-upload text-4xl sm:text-6xl mt-1 sm:mt-2 mb-1 sm:mb-2"></i>
              <span className="text-sm sm:text-base">Click to upload files</span>
            </label>
            <p className="text-gray-400 mt-2">
              Maximum file size 10 MB. Allowed file types: ZIP, XLS, XLSX, CSV
            </p>
          </div>

          {/* File List */}
          {files.map((fileObj, index) => (
            <div key={index} className="bg-gray-100 p-3 sm:p-4 mb-3 sm:mb-4 rounded-lg">
              <div className="flex justify-between items-start">
                <div className='flex-1 min-w-0 pr-4'>
                  <p className="text-xs sm:text-sm font-bold truncate max-w-full">{fileObj.name}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {(fileObj.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                <button
                  className="text-red-500 text-xl sm:text-2xl flex-shrink-0 hover:text-red-600 transition-colors"
                  onClick={() => handleFileRemove(index)}
                  aria-label='Remove file'
                >
                  &times;
                </button>
              </div>
            </div>
          ))}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row justify-between gap-3 sm:gap-4 mt-4">
            <button
              className="btn bg-gray-300 text-black font-semibold text-sm sm:text-base py-2 px-4 rounded-lg w-full sm:w-auto"
              onClick={handleClear}
            >
              <i className="fa-solid fa-xmark mr-2"></i>
              Clear
            </button>
            <button
              className={`btn ${files.length === 0 || totalFileSize / 1024 / 1024 > 10 ? "bg-gray-300 cursor-not-allowed" : "bg-black hover:bg-gray-800"} text-white transition-colors duration-300 text-sm sm:text-base py-2 px-4 rounded-lg w-full sm:w-auto`}
              onClick={handleUpload}
              disabled={files.length === 0 || totalFileSize / 1024 / 1024 > 10}
            >
              <i className="fa-solid fa-arrow-up-from-bracket mr-2"></i>
              Upload files
            </button>
          </div>
        </div>
      </div>

      {/* General Modal */}
      <GeneralModal
        isVisible={generalModal.isVisible}
        message={generalModal.message}
        onClose={handleCloseGeneralModal}
        isSuccess={generalModal.isSuccess}
      />

      {/* Upload Progress Modal */}
      <UploadProgressModal
        isVisible={uploadProgressModalVisible}
        progress={uploadProgress}
        totalFiles={files.length}
        totalSize={totalFileSize}
        uploadedSize={uploadedSize}
        onCancel={handleCancelUpload}
        isUploading={isUploading}
      />
    </>
  );
}